import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Heart, Sun, Moon, Users, MapPin, Home, Briefcase, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dream life quiz questions
const dreamQuestions = [
  {
    id: 1,
    question: "What kind of morning sounds perfect to you?",
    emoji: "🌅",
    options: [
      { text: "Wake up early, go for a run", type: "active", icon: "🏃" },
      { text: "Sleep in, relaxed breakfast", type: "chill", icon: "😴" },
      { text: "Wake up with pets/animals", type: "nature", icon: "🐕" },
      { text: "Start working on something exciting", type: "creative", icon: "💡" }
    ]
  },
  {
    id: 2,
    question: "Where would your dream home be?",
    emoji: "🏡",
    options: [
      { text: "Big city with lots to do", type: "city", icon: "🏙️" },
      { text: "Beach house by the ocean", type: "beach", icon: "🏖️" },
      { text: "Countryside with animals", type: "nature", icon: "🌲" },
      { text: "Cozy suburb with friends nearby", type: "suburban", icon: "🏘️" }
    ]
  },
  {
    id: 3,
    question: "How would you spend a perfect weekend?",
    emoji: "🎉",
    options: [
      { text: "Hanging out with lots of friends", type: "social", icon: "👥" },
      { text: "Adventure - hiking, exploring", type: "adventure", icon: "🥾" },
      { text: "Creating something (art, music, coding)", type: "creative", icon: "🎨" },
      { text: "Relaxing and watching movies", type: "chill", icon: "🍿" }
    ]
  },
  {
    id: 4,
    question: "What kind of work sounds most fun?",
    emoji: "💼",
    options: [
      { text: "Helping people every day", type: "helper", icon: "❤️" },
      { text: "Building/creating cool things", type: "builder", icon: "🔧" },
      { text: "Being your own boss", type: "entrepreneur", icon: "👔" },
      { text: "Working with animals or nature", type: "nature", icon: "🌿" }
    ]
  },
  {
    id: 5,
    question: "What matters most to you?",
    emoji: "⭐",
    options: [
      { text: "Having lots of free time", type: "freedom", icon: "🕐" },
      { text: "Making a lot of money", type: "wealth", icon: "💰" },
      { text: "Being famous or well-known", type: "fame", icon: "🌟" },
      { text: "Making a difference in the world", type: "impact", icon: "🌍" }
    ]
  },
  {
    id: 6,
    question: "Dream vacation?",
    emoji: "✈️",
    options: [
      { text: "Theme parks and excitement", type: "adventure", icon: "🎢" },
      { text: "Beach resort, just relaxing", type: "chill", icon: "🏝️" },
      { text: "Exploring a new country", type: "explorer", icon: "🗺️" },
      { text: "Camping in nature", type: "nature", icon: "⛺" }
    ]
  }
];

// Dream life profiles based on answers
const dreamProfiles = {
  active_city: {
    title: "City Adventurer",
    description: "You want an exciting life in a big city, staying active and always having something to do!",
    lifestyle: "Fast-paced city life with lots of activities and social events",
    careers: ["Business Professional", "Marketing", "Fitness Trainer", "Event Planner"]
  },
  creative_chill: {
    title: "Creative Free Spirit",
    description: "You value your freedom and creativity. You want time to create and explore your passions.",
    lifestyle: "Flexible schedule, creative work, work from anywhere",
    careers: ["Artist", "Writer", "Freelance Designer", "Content Creator"]
  },
  nature_helper: {
    title: "Nature Guardian",
    description: "You love animals and nature. You want to spend time outdoors and help living things.",
    lifestyle: "Peaceful life surrounded by nature and animals",
    careers: ["Veterinarian", "Marine Biologist", "Park Ranger", "Environmental Scientist"]
  },
  social_impact: {
    title: "World Changer",
    description: "You want to make a big difference! You care about people and want to help make the world better.",
    lifestyle: "Meaningful work, lots of connections, making impact",
    careers: ["Doctor", "Teacher", "Social Worker", "Non-profit Leader"]
  }
};

export default function MSDreamLifePage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [dreamProfile, setDreamProfile] = useState(null);

  const handleAnswer = (type) => {
    const newAnswers = { ...answers, [currentQuestion]: type };
    setAnswers(newAnswers);

    if (currentQuestion < dreamQuestions.length - 1) {
      setTimeout(() => setCurrentQuestion(prev => prev + 1), 300);
    } else {
      calculateDreamProfile(newAnswers);
    }
  };

  const calculateDreamProfile = (allAnswers) => {
    // Simple logic to determine profile
    const types = Object.values(allAnswers);
    const hasNature = types.includes('nature');
    const hasCreative = types.includes('creative');
    const hasImpact = types.includes('impact') || types.includes('helper');
    const hasCity = types.includes('city');

    let profile;
    if (hasNature && hasImpact) {
      profile = 'nature_helper';
    } else if (hasCreative) {
      profile = 'creative_chill';
    } else if (hasImpact) {
      profile = 'social_impact';
    } else {
      profile = 'active_city';
    }

    setDreamProfile(dreamProfiles[profile]);
    setShowResults(true);
    saveResults(profile);
  };

  const saveResults = async (profile) => {
    try {
      await axios.post(`${API}/quiz/dream-life-result`, {
        profile_type: profile,
        answers: answers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to save');
    }
  };

  const progress = ((currentQuestion + 1) / dreamQuestions.length) * 100;

  if (showResults && dreamProfile) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center mb-4">
            <Heart className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Your Dream Life!</h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="p-8 rounded-2xl bg-gradient-to-br from-pink-500 to-purple-600 mt-6 mb-6"
          >
            <h2 className="text-2xl font-bold text-white mb-2">{dreamProfile.title}</h2>
            <p className="text-white/90">{dreamProfile.description}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="glass-card p-6 mb-6 text-left"
          >
            <h3 className="text-white font-semibold mb-2">Your Ideal Lifestyle:</h3>
            <p className="text-slate-300">{dreamProfile.lifestyle}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="glass-card p-6 mb-6"
          >
            <h3 className="text-white font-semibold mb-3">Careers That Match:</h3>
            <div className="flex flex-wrap justify-center gap-2">
              {dreamProfile.careers.map((career, i) => (
                <span key={i} className="px-4 py-2 bg-pink-500/30 rounded-full text-pink-200">
                  {career}
                </span>
              ))}
            </div>
          </motion.div>

          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => {
                setCurrentQuestion(0);
                setAnswers({});
                setShowResults(false);
              }}
              variant="outline"
              className="border-slate-600 text-slate-300"
            >
              Try Again
            </Button>
            <Button
              onClick={() => navigate('/ms/careers')}
              className="bg-pink-500 hover:bg-pink-600"
            >
              Explore Careers
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  const question = dreamQuestions[currentQuestion];

  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
            <Heart className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Dream Life Quiz</h1>
            <p className="text-slate-300">Imagine your perfect future!</p>
          </div>
        </div>

        {/* Progress */}
        <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-pink-500 to-purple-500"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
          />
        </div>
        <p className="text-slate-300 text-sm mt-2">
          Question {currentQuestion + 1} of {dreamQuestions.length}
        </p>
      </motion.div>

      {/* Question */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentQuestion}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          className="glass-card p-6"
        >
          <div className="text-center mb-6">
            <span className="text-5xl mb-4 block">{question.emoji}</span>
            <h2 className="text-xl font-semibold text-white">{question.question}</h2>
          </div>

          <div className="space-y-3">
            {question.options.map((option, index) => (
              <motion.button
                key={index}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleAnswer(option.type)}
                className="w-full p-4 rounded-xl bg-slate-800 text-slate-200 hover:bg-slate-700 text-left flex items-center gap-3"
              >
                <span className="text-2xl">{option.icon}</span>
                <span>{option.text}</span>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>

      {currentQuestion > 0 && (
        <Button
          variant="ghost"
          onClick={() => setCurrentQuestion(prev => prev - 1)}
          className="mt-4 text-slate-300"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back
        </Button>
      )}
    </div>
  );
}
